<template>
  <div class="page-container reveal push-page">
    <div class="push-hero" style="background: linear-gradient(135deg, var(--el-color-primary) 0%, #4a90e2 100%); color: white; padding: 24px; border-radius: 12px; margin-bottom: 24px;">
      <div>
        <div class="push-eyebrow" style="opacity: 0.8; font-weight: 600; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 0.5px;">Communication Opérationnelle</div>
        <h2 style="margin: 8px 0;">Push Center</h2>
        <p style="opacity: 0.9;">Envoie des annonces manuelles et surveille l’historique d’envoi des notifications Push.</p>
      </div>
      <div class="push-hero-stats" style="display: flex; gap: 24px; margin-top: 16px;">
        <div class="hero-stat">
          <strong style="font-size: 1.5rem;">{{ logs.length }}</strong>
          <span style="opacity: 0.8; margin-left: 8px;">logs chargés</span>
        </div>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="push-tabs">
      <el-tab-pane v-if="canBroadcast" label="Envoi Manuel" name="broadcast">
        <div class="push-card">
          <div class="card-head">
            <div>
              <h3>Envoyer un Push manuel</h3>
              <p>Utile pour incident, maintenance, alerte service ou annonce ponctuelle.</p>
            </div>
          </div>

          <div class="broadcast-grid" style="display: grid; grid-template-columns: 2fr 1fr; gap: 24px; margin-top: 24px;">
            <el-form label-position="top">
              <el-form-item label="Audience">
                <el-select v-model="broadcast.target_type" style="width: 100%">
                  <el-option label="Tous les utilisateurs" value="all" />
                  <el-option label="Clients" value="client" />
                  <el-option label="Supports" value="support" />
                  <el-option label="Admins" value="admin" />
                  <el-option label="Numéro spécifique" value="specific_number" />
                  <el-option label="Liste de numéros" value="specific_list" />
                </el-select>
              </el-form-item>

              <el-form-item v-if="broadcast.target_type === 'specific_number'" label="Numéro">
                <el-input v-model="broadcast.phone" placeholder="Ex: 70000000" />
              </el-form-item>

              <el-form-item v-if="broadcast.target_type === 'specific_list'" label="Liste de numéros">
                <el-input
                  v-model="broadcast.phoneListText"
                  type="textarea"
                  :rows="5"
                  placeholder="Un numéro par ligne ou séparés par virgule"
                />
              </el-form-item>

              <el-form-item label="Titre">
                <el-input
                  v-model="broadcast.title"
                  placeholder="Titre de la notification"
                  maxlength="100"
                />
              </el-form-item>

              <el-form-item label="Message">
                <el-input
                  v-model="broadcast.message"
                  type="textarea"
                  :rows="6"
                  maxlength="500"
                  show-word-limit
                  placeholder="Décris l'incident, la maintenance ou l'annonce."
                />
              </el-form-item>

              <div class="broadcast-actions" style="margin-top: 16px;">
                <el-button type="primary" :loading="sendingBroadcast" @click="submitBroadcast">
                  Envoyer le Push
                </el-button>
              </div>
            </el-form>

            <div class="broadcast-preview">
              <h4>Aperçu</h4>
              <div class="preview-bubble">
                <strong>{{ broadcast.title || 'Titre' }}</strong><br/>
                <span class="preview-bubble-text">{{ previewText }}</span>
              </div>
              <div v-if="lastBroadcast" class="broadcast-result">
                <h4>Dernier envoi</h4>
                <p><strong>ID:</strong> {{ lastBroadcast.broadcast_id }}</p>
                <p><strong>Demandés:</strong> {{ lastBroadcast.requested }}</p>
                <p><strong>Envoyés:</strong> {{ lastBroadcast.sent }}</p>
                <p><strong>Échecs:</strong> {{ lastBroadcast.failed }}</p>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="canReadLogs" label="Logs" name="logs">
        <div class="push-card">
          <div class="card-head logs-head">
            <div>
              <h3>Historique Push</h3>
              <p>Trace des envois.</p>
            </div>
            <div class="log-filters">
              <el-input v-model="filters.type" placeholder="Type exact" clearable style="width: 180px" />
              <el-date-picker
                v-model="filters.dateRange"
                type="daterange"
                range-separator="À"
                start-placeholder="Début"
                end-placeholder="Fin"
                value-format="YYYY-MM-DD"
                style="width: 240px"
              />
              <el-button @click="loadLogs" :loading="loadingLogs">Rafraîchir</el-button>
            </div>
          </div>

          <el-table :data="logs" style="width: 100%" v-loading="loadingLogs" size="small">
            <el-table-column label="Date" width="160">
              <template #default="{ row }">
                {{ fmtDate(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column prop="user_id" label="User ID" width="160" show-overflow-tooltip />
            <el-table-column label="Type" width="180">
              <template #default="{ row }">
                <el-tag size="small" :type="row.type === 'admin.broadcast' ? 'warning' : 'info'">
                  {{ formatType(row.type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="title" label="Titre" width="200" show-overflow-tooltip />
            <el-table-column prop="body" label="Message" show-overflow-tooltip />
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watchEffect } from 'vue'
import { ElNotification } from 'element-plus'
import { listPushLogs, sendPushBroadcast } from '../services/adminApi'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const { hasPermission } = useAdminPermissions()

const notify = (type, title, message) => {
  ElNotification({ title, message, type })
}

const canBroadcast = computed(() => hasPermission('sms.broadcast.send'))
const canReadLogs = computed(() => hasPermission('sms.logs.read'))

const activeTab = ref('logs')
watchEffect(() => {
  if (canBroadcast.value && activeTab.value === 'logs') {
    activeTab.value = 'broadcast'
  }
})

const broadcast = ref({
  target_type: 'client',
  phone: '',
  phoneListText: '',
  title: '',
  message: '',
})
const sendingBroadcast = ref(false)
const lastBroadcast = ref(null)

const previewText = computed(() => {
  return broadcast.value.message || 'Votre message ici...'
})

const submitBroadcast = async () => {
  if (!broadcast.value.title || broadcast.value.title.length < 3) {
    return notify('warning', 'Erreur', 'Titre trop court.')
  }
  if (!broadcast.value.message || broadcast.value.message.length < 3) {
    return notify('warning', 'Erreur', 'Message trop court.')
  }

  let phones = []
  if (broadcast.value.target_type === 'specific_list') {
    phones = broadcast.value.phoneListText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
    if (!phones.length) return notify('warning', 'Erreur', 'Liste vide.')
  }

  const payload = {
    target_type: broadcast.value.target_type,
    title: broadcast.value.title,
    message: broadcast.value.message,
  }
  if (broadcast.value.target_type === 'specific_number') payload.phone = broadcast.value.phone
  if (broadcast.value.target_type === 'specific_list') payload.phones = phones

  try {
    sendingBroadcast.value = true
    lastBroadcast.value = await sendPushBroadcast(payload)
    notify('success', 'Broadcast Push', `${lastBroadcast.value.sent} Push envoyés.`)
    broadcast.value.message = ''
  } catch (error) {
    notify('error', 'Broadcast Push', error.response?.data?.detail || error.message)
  } finally {
    sendingBroadcast.value = false
  }
}

const logs = ref([])
const loadingLogs = ref(false)
const filters = ref({
  type: '',
  dateRange: []
})

const typeLabels = {
  'admin.broadcast': 'Annonce Admin',
  'waitlist_available': 'Artisan dispo (File)',
  'mission.assigned': 'Mission assignée',
  'mission.accepted': 'Mission acceptée',
  'mission.arrived': 'Artisan arrivé',
  'mission.en_route': 'Artisan en route',
  'mission.completed': 'Mission terminée',
  'quote.ready': 'Devis prêt',
  'payment.confirmed': 'Paiement confirmé',
  'chat.new_message': 'Nouveau message',
  'dispute.opened': 'Litige ouvert',
  'sav.opened': 'SAV ouvert'
}
const formatType = (type) => typeLabels[type] || type

const loadLogs = async () => {
  if (!canReadLogs.value) return
  loadingLogs.value = true
  try {
    logs.value = await listPushLogs({
      limit: 100,
      type: filters.value.type || undefined,
      start_date: filters.value.dateRange?.[0] ? `${filters.value.dateRange[0]}T00:00:00Z` : undefined,
      end_date: filters.value.dateRange?.[1] ? `${filters.value.dateRange[1]}T23:59:59Z` : undefined,
    })
  } catch (error) {
    notify('error', 'Logs Push', error.message)
  } finally {
    loadingLogs.value = false
  }
}

const fmtDate = (d) => {
  if (!d) return '-'
  const dt = new Date(d)
  return dt.toLocaleString('fr-FR', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(() => {
  if (canReadLogs.value) loadLogs()
})
</script>

<style scoped>
.push-page { display: flex; flex-direction: column; gap: 1.5rem; max-width: 1000px; margin: 0 auto; }
.push-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(9, 36, 70, 0.95), rgba(7, 78, 96, 0.9));
  border: 1px solid rgba(255,255,255,0.08);
}
.push-hero h2 { margin: 0 0 0.35rem; color: #fff; font-size: 1.9rem; }
.push-hero p { margin: 0; color: rgba(255,255,255,0.75); max-width: 58ch; }
.push-eyebrow { text-transform: uppercase; letter-spacing: 0.14em; color: #7dd3fc; font-size: 0.75rem; margin-bottom: 0.5rem; }
.push-hero-stats { display: flex; gap: 0.85rem; align-items: stretch; }
.hero-stat {
  min-width: 110px;
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.hero-stat strong { color: #fff; font-size: 1.5rem; }
.hero-stat span { color: rgba(255,255,255,0.72); font-size: 0.82rem; }

.push-card {
  padding: 1.25rem;
  border-radius: 22px;
  background: var(--glass-bg, var(--el-bg-color-overlay));
  border: 1px solid var(--glass-border, var(--el-border-color-light));
}
.card-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}
.card-head.logs-head {
  align-items: center;
}
.card-head h3 { margin: 0; }
.card-head p { margin: 0.35rem 0 0; color: var(--text-muted, var(--el-text-color-secondary)); }

.broadcast-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.75fr);
  gap: 1rem;
}
.broadcast-preview {
  padding: 1rem;
  border-radius: 18px;
  background: var(--glass-bg, var(--el-bg-color-page));
  border: 1px solid var(--glass-border, var(--el-border-color-light));
}
.preview-bubble {
  margin-top: 0.75rem;
  padding: 1rem;
  border-radius: 16px 16px 16px 6px;
  background: rgba(14, 116, 144, 0.18);
  border: 1px solid rgba(125, 211, 252, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: var(--text-main, var(--el-text-color-primary));
}
.preview-bubble-text { color: var(--el-text-color-regular); }
.broadcast-result {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(128,128,128,0.2);
}
.broadcast-actions { margin-top: 1rem; display: flex; justify-content: flex-end; }
.log-filters { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }

@media (max-width: 980px) {
  .push-hero { flex-direction: column; }
  .push-hero-stats { width: 100%; }
  .broadcast-grid { grid-template-columns: 1fr; }
}
</style>
